// The sample heat load report, shown page by page. Shown, not given: there is
// no download link, the images are screen size, and the values in them are
// blurred. Tap a page to see it larger; arrows or swipe to move between pages.

import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { SAMPLE_REPORT } from '../data/designerLibrary';

export default function SampleReport() {
  const pages = SAMPLE_REPORT.pages;
  const [open, setOpen] = useState(null); // index into pages, or null
  const [touchX, setTouchX] = useState(null);

  useEffect(() => {
    if (open == null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(null);
      if (e.key === 'ArrowRight') setOpen((i) => Math.min(pages.length - 1, i + 1));
      if (e.key === 'ArrowLeft') setOpen((i) => Math.max(0, i - 1));
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, pages.length]);

  const step = (d) => setOpen((i) => Math.max(0, Math.min(pages.length - 1, i + d)));

  return (
    <div id="sample" className="scroll-mt-28 rounded-2xl border border-gray-200 bg-gray-50 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-base font-bold text-gray-900">{SAMPLE_REPORT.title}</p>
        <p className="text-xs text-gray-500">{pages.length} pages · tap to view</p>
      </div>
      <p className="mt-1 text-xs leading-relaxed text-gray-600">{SAMPLE_REPORT.note}</p>
      <ul className="mt-3 flex gap-3 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {pages.map((p, i) => (
          <li key={p.n} className="shrink-0">
            <button type="button" onClick={() => setOpen(i)}
              className="group block w-[132px] text-left sm:w-[150px]" aria-label={`View page ${p.n}: ${p.label}`}>
              <img src={SAMPLE_REPORT.thumb(p.n)} alt="" width="150" height="212" loading="lazy" draggable="false"
                className="h-[187px] w-full rounded-lg border border-gray-200 bg-white object-cover object-top shadow-sm transition group-hover:shadow-md sm:h-[212px]" />
              <span className="mt-1.5 block text-[11px] leading-snug text-gray-700">
                <b className="text-gray-900">{p.n}.</b> {p.label}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {open != null && (
        <div role="dialog" aria-modal="true" aria-label={`Sample report, page ${pages[open].n}`}
          className="fixed inset-0 z-[60] flex flex-col bg-gray-900/95"
          onClick={() => setOpen(null)}>
          <div className="flex items-center justify-between px-4 py-3 text-white" onClick={(e) => e.stopPropagation()}>
            <p className="text-sm font-semibold">Page {pages[open].n} of {pages.length} · {pages[open].label}</p>
            <button type="button" onClick={() => setOpen(null)} aria-label="Close"
              className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-white/10">
              <X className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div className="relative flex min-h-0 flex-1 items-center justify-center px-2 pb-4"
            onTouchStart={(e) => setTouchX(e.touches[0].clientX)}
            onTouchEnd={(e) => {
              if (touchX == null) return;
              const dx = e.changedTouches[0].clientX - touchX;
              if (Math.abs(dx) > 50) step(dx < 0 ? 1 : -1);
              setTouchX(null);
            }}>
            <img src={SAMPLE_REPORT.src(pages[open].n)} alt={`Sample heat load report, page ${pages[open].n}: ${pages[open].label}`}
              draggable="false" onContextMenu={(e) => e.preventDefault()} onClick={(e) => e.stopPropagation()}
              className="max-h-full max-w-full select-none rounded bg-white object-contain shadow-2xl" />
            {open > 0 && (
              <button type="button" onClick={(e) => { e.stopPropagation(); step(-1); }} aria-label="Previous page"
                className="absolute left-2 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-gray-900 shadow">
                <ChevronLeft className="h-6 w-6" aria-hidden="true" />
              </button>
            )}
            {open < pages.length - 1 && (
              <button type="button" onClick={(e) => { e.stopPropagation(); step(1); }} aria-label="Next page"
                className="absolute right-2 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-gray-900 shadow">
                <ChevronRight className="h-6 w-6" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
